import React, { createContext, useContext, useEffect } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";

const GlobalSettingsContext = createContext({});

export const useGlobalSettings = () => useContext(GlobalSettingsContext);

export const GlobalSettingsProvider = ({ children }) => {
  // Load and apply global settings
  useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        const globalSettingsRef = doc(db, "globalSettings", "siteSettings");
        const settingsDoc = await getDoc(globalSettingsRef);

        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          applySettings(settings);
        }
      } catch (error) {
        console.error("Error loading global settings:", error);
        // Fall back to localStorage if Firebase fails
        loadLocalSettings();
      }
    };

    // Apply settings to the document
    const applySettings = (settings) => {
      // Apply accent color
      if (settings.accentColor) {
        document.documentElement.style.setProperty(
          "--color-accent",
          settings.accentColor.value
        );
        document.documentElement.style.setProperty(
          "--color-accent-hover",
          settings.accentColor.hover
        );
      }

      // Apply font size
      if (settings.fontSize) {
        document.documentElement.style.fontSize = `${settings.fontSize}px`;
      }

      // Apply font families
      if (settings.fontSettings) {
        document.documentElement.style.setProperty(
          "--font-display-heading",
          settings.fontSettings.displayHeading.value
        );
        document.documentElement.style.setProperty(
          "--font-heading",
          settings.fontSettings.headings.value
        );
        document.documentElement.style.setProperty(
          "--font-subheading",
          settings.fontSettings.subheading?.value || settings.fontSettings.headings.value
        );
        document.documentElement.style.setProperty(
          "--font-body",
          settings.fontSettings.body.value
        );
        document.documentElement.style.setProperty(
          "--font-ui",
          settings.fontSettings.ui.value
        );
      }

      // Apply custom fonts
      if (settings.customFonts && settings.customFonts.length > 0) {
        settings.customFonts.forEach((font) => {
          // Check if font-face already exists
          const existingStyle = document.querySelector(
            `style[data-font-name="${font.name}"]`
          );
          if (!existingStyle) {
            const styleElement = document.createElement("style");
            styleElement.textContent = `
              @font-face {
                font-family: '${font.name}';
                src: url(${font.data});
                font-weight: normal;
                font-style: normal;
              }
            `;
            styleElement.setAttribute("data-font-name", font.name);
            document.head.appendChild(styleElement);
          }
        });
      }
    };

    // Load settings from localStorage as fallback
    const loadLocalSettings = () => {
      const savedColor = localStorage.getItem("accent-color");
      if (savedColor) {
        const color = JSON.parse(savedColor);
        document.documentElement.style.setProperty(
          "--color-accent",
          color.value
        );
        document.documentElement.style.setProperty(
          "--color-accent-hover",
          color.hover
        );
      }

      const savedFontSize = localStorage.getItem("font-size");
      if (savedFontSize) {
        document.documentElement.style.fontSize = `${savedFontSize}px`;
      }

      const savedFontSettings = localStorage.getItem("font-settings");
      if (savedFontSettings) {
        const fontSettings = JSON.parse(savedFontSettings);
        if (fontSettings.displayHeading) {
          document.documentElement.style.setProperty(
            "--font-display-heading",
            fontSettings.displayHeading.value
          );
        }
        if (fontSettings.headings) {
          document.documentElement.style.setProperty(
            "--font-heading",
            fontSettings.headings.value
          );
        }
        if (fontSettings.subheading) {
          document.documentElement.style.setProperty(
            "--font-subheading",
            fontSettings.subheading.value
          );
        }
        if (fontSettings.body) {
          document.documentElement.style.setProperty(
            "--font-body",
            fontSettings.body.value
          );
        }
        if (fontSettings.ui) {
          document.documentElement.style.setProperty(
            "--font-ui",
            fontSettings.ui.value
          );
        }
      }
    };

    // Initial load
    loadGlobalSettings();

    // Set up real-time listener for settings changes
    const unsubscribe = onSnapshot(
      doc(db, "globalSettings", "siteSettings"),
      (doc) => {
        if (doc.exists()) {
          const settings = doc.data();
          applySettings(settings);
        }
      },
      (error) => {
        console.error("Error listening to settings changes:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <GlobalSettingsContext.Provider value={{}}>
      {children}
    </GlobalSettingsContext.Provider>
  );
};
